<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from "$lib/components/ui/command"
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { Popover, PopoverButton, PopoverContent } from "$lib/components/ui/popover"
  import { RadioGroup, RadioGroupItem } from "$lib/components/ui/radio-group"
  import { Textarea } from "$lib/components/ui/textarea"
  import type { _response } from "$lib/data/type/_response"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { getLocalTimeZone, today, type DateValue } from "@internationalized/date"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"
  import type { Order } from "../_types"
  import type { Relation } from "$lib/data/schema/_relation"
  import type { RelationStr } from "$lib/data/schema/_relation-str"

  const FormSchema = z.object({
    date: z.custom<DateValue>().default(today(getLocalTimeZone())),
    number: z.string().default(""),
    type_id: z.string().default(""),
    type_name: z.string().default(""),
    customer_id: z.number().default(0),
    customer_name: z.string().default(""),
    description: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  let {
    open,
    setOpen,
    select,
    refresh,
  }: {
    open: boolean
    setOpen: (value: boolean) => void
    select: string
    refresh: () => void | Promise<void>
  } = $props()

  const prefix = $derived(page.params.prefix ?? "0")

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))
  const formWatch = form.useSelector((state) => state.values)

  const types = createQuery(() => ({
    enabled: open,
    queryKey: ["order-dialog-types", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<RelationStr[]>>(`/api/${prefix}/order/data/master-type`)
      return response.data.result!
    },
  }))
  const customerSearch = createSearch()
  const customers = createQuery(() => ({
    enabled: open,
    queryKey: ["order-dialog-customers", {
      prefix,
      search: customerSearch.debounced
    }],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/order/data/master-customer`, {
        params: {
          search: customerSearch.debounced
        },
      })
      return response.data.result!
    },
  }))

  const query = createQuery(() => ({
    enabled: open,
    queryKey: ["order-dialog-form", prefix, select],
    queryFn: async () => {

      const response = await httpClient.get<_response<Order>>(`/api/${prefix}/order/data/${select}`)
      const responseData = response.data.result!

      form.reset(FormEmpty)
      form.setFieldValue("date", responseData.date.toDate())
      form.setFieldValue("number", responseData.number)
      form.setFieldValue("type_id", responseData.type?.id ?? "")
      form.setFieldValue("type_name", responseData.type?.name ?? "")
      form.setFieldValue("customer_id", responseData.customer.id as number)
      form.setFieldValue("customer_name", responseData.customer.name)
      form.setFieldValue("description", responseData.description ?? "")

      return ""
    },
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const response = await httpClient.post<_response<null>>(`/api/${prefix}/order/data`, {
        ...data,
        date: data.date.format("yyyy-MM-dd"),
      })
      const responseData = response.data

      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) return

      toast.success(responseData.message)
      form.reset(FormEmpty)
      setOpen(false)

      void refresh()
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message
      })
    }
  }))

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Order Baru</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    <Loader show={query.isFetching || mutation.isPending || !types.data || !customers.data}/>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-6"
      onsubmit={(event) => {
        event.preventDefault()
        mutation.mutate(formWatch.current)
      }}>
      <div class="space-y-3">
        <div class="flex gap-3">
          <form.Field name="date">
            {#snippet children(field)}
              <div class="flex-1">
                <FieldLabel>Tanggal</FieldLabel>
                <Input
                  type="text"
                  name={field.name}
                  value={field.state.value.format("dd MMMM yyyy")}
                  readonly
                  class="font-mono"/>
                <FieldMessage
                  name={field.name}
                  form={form}/>
              </div>
            {/snippet}
          </form.Field>
          <form.Field name="number">
            {#snippet children(field)}
              <div class="flex-1">
                <FieldLabel>Nomor</FieldLabel>
                <Input
                  type="text"
                  name={field.name}
                  value={field.state.value}
                  readonly
                  class="font-mono"/>
                <FieldMessage
                  name={field.name}
                  form={form}/>
              </div>
            {/snippet}
          </form.Field>
        </div>
        <form.Field name="type_id">
          {#snippet children(field)}
            <div>
              <FieldLabel>Tipe</FieldLabel>
              <RadioGroup
                name={field.name}
                value={field.state.value}
                class="flex gap-4">
                {#each types.data ?? [] as item (item.id)}
                  <label class="flex items-center gap-2 text-sm">
                    <RadioGroupItem
                      value={item.id}
                      onclick={() => {
                        form.setFieldValue("type_id", item.id)
                        form.setFieldValue("type_name", item.name)
                      }}
                    />
                    <span>{item.name}</span>
                  </label>
                {/each}
              </RadioGroup>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="customer_name">
          {#snippet children(field)}
            <div>
              <FieldLabel>Customer</FieldLabel>
              <Popover>
                <PopoverButton value={field.state.value || "Pilih Customer"}/>
                <PopoverContent>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Cari..."
                      value={customerSearch.state}
                      oninput={(event) => customerSearch.set(event.currentTarget.value)}/>
                    <CommandList class="min-h-20 max-h-40">
                      <CommandEmpty>Tidak ada data</CommandEmpty>
                      <CommandGroup>
                        {#each customers.data ?? [] as customer (customer.id)}
                          <CommandItem
                            value={customer.name}
                            onclick={() => {
                              form.setFieldValue("customer_id", customer.id)
                              form.setFieldValue("customer_name", customer.name)
                              document.keyEsc()
                            }}>
                            {customer.name}
                          </CommandItem>
                        {/each}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FieldMessage
                name="customer_id"
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="description">
          {#snippet children(field)}
            <div>
              <FieldLabel>Keterangan</FieldLabel>
              <Textarea
                name={field.name}
                value={field.state.value}
                class="font-mono"
                oninput={(event) => field.handleChange(event.currentTarget.value)}/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <DialogFooter>
        <div class="flex-1"></div>
        <Button
          type="button"
          variant="outline"
          onclick={() => setOpen(false)}>
          Batal
        </Button>
        <Button type="submit">Lanjut</Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
